import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Send,
  AlertCircle,
  CheckCircle,
  Building2,
  FileText,
} from 'lucide-react';
import { createPaymentRequest } from '../../services/PaymentService';

interface Project {
  _id: string;
  title: string;
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const CreatePaymentRequest: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const [formData, setFormData] = useState({
    projectId: '',
    amount: '',
    currency: 'USD',
    description: '',
    dueDate: '',
  });

  const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};


  const API_BASE_URL = getApiBaseUrl()

  useEffect(() => {
    // Only admin should be able to create payment requests
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchProjects();
  }, [user, navigate]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      // Mock data for demonstration
      setProjects([
        {
          _id: 'proj_1',
          title: 'E-commerce Platform',
          client: {
            _id: 'client_1',
            firstName: 'John',
            lastName: 'Smith',
            email: 'john.smith@example.com',
          },
        },
        {
          _id: 'proj_2',
          title: 'Mobile App Development',
          client: {
            _id: 'client_1',
            firstName: 'John',
            lastName: 'Smith',
            email: 'john.smith@example.com',
          },
        },
        {
          _id: 'proj_3',
          title: 'Website Redesign',
          client: {
            _id: 'client_2',
            firstName: 'Sarah',
            lastName: 'Johnson',
            email: 'sarah.johnson@example.com',
          },
        },
      ]);
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    setFormData((prev) => ({ ...prev, projectId }));

    if (projectId) {
      const project = projects.find((p) => p._id === projectId);
      if (project) {
        setSelectedProject(project);
      }
    } else {
      setSelectedProject(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!selectedProject) {
      setError('Please select a project');
      setLoading(false);
      return;
    }

    try {
      const paymentRequestData = {
        project: formData.projectId, // ✅ FIXED
        client: selectedProject.client._id, // ✅ FIXED
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        description: formData.description,
        dueDate: formData.dueDate,
      };

      await createPaymentRequest(paymentRequestData);
      setSuccess(true);

      setFormData({
        projectId: '',
        amount: '',
        currency: 'USD',
        description: '',
        dueDate: '',
      });
      setSelectedProject(null);

      setTimeout(() => {
        navigate('/dashboard/admin/payment-requests');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to create payment request');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const currencies = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'GHS', label: 'GHS (₵)' },
    { value: 'NGN', label: 'NGN (₦)' },
  ];

  if (success) {
    return (
      <div className="space-y-6">
        <div
          className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center`}
        >
          <CheckCircle
            className={`w-16 h-16 mx-auto mb-4 ${
              theme === 'dark' ? 'text-green-400' : 'text-green-500'
            }`}
          />
          <h2
            className={`text-2xl font-bold mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Payment Request Created
          </h2>
          <p
            className={`mb-6 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            The payment request has been sent to the client successfully.
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/dashboard/admin/payment-requests"
              className={`px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-lg transition-colors duration-200`}
            >
              View All Requests
            </Link>
            <button
              onClick={() => setSuccess(false)}
              className={`px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-gray-100 hover:bg-gray-200'
              } ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              } rounded-lg transition-colors duration-200`}
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/dashboard/admin/payment-requests"
          className={`p-2 ${
            theme === 'dark'
              ? 'text-gray-300 hover:text-white hover:bg-gray-700'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          } rounded-lg transition-colors duration-200`}
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1
            className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
          >
            Create Payment Request
          </h1>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mt-1`}
          >
            Send a payment request to a client
          </p>
        </div>
      </div>

      {/* Form */}
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700`}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div
              className={`${
                theme === 'dark'
                  ? 'bg-red-900/20 border-red-800'
                  : 'bg-red-50 border-red-200'
              } border rounded-lg p-4`}
            >
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 mr-2" />
                <span
                  className={`${
                    theme === 'dark' ? 'text-red-300' : 'text-red-700'
                  } text-sm`}
                >
                  {error}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="projectId"
                className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } mb-2`}
              >
                Project *
              </label>
              <select
                id="projectId"
                name="projectId"
                required
                value={formData.projectId}
                onChange={handleProjectChange}
                className={`w-full px-3 py-2 border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.title} - {project.client.firstName}{' '}
                    {project.client.lastName}
                  </option>
                ))}
              </select>
            </div>

            {selectedProject && (
              <div
                className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <Building2
                    className={`w-5 h-5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  />
                  <span
                    className={`font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Client: {selectedProject.client.firstName}{' '}
                    {selectedProject.client.lastName}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <FileText
                    className={`w-5 h-5 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  />
                  <span
                    className={`${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Project: {selectedProject.title}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="amount"
                  className={`block text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  } mb-2`}
                >
                  Amount *
                </label>
                <div className="relative">
                  <div
                    className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    required
                    min="1"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    className={`pl-10 w-full px-3 py-2 border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="currency"
                  className={`block text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  } mb-2`}
                >
                  Currency *
                </label>
                <select
                  id="currency"
                  name="currency"
                  required
                  value={formData.currency}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  {currencies.map((currency) => (
                    <option key={currency.value} value={currency.value}>
                      {currency.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="description"
                className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } mb-2`}
              >
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                required
                rows={3}
                value={formData.description}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Describe what this payment is for..."
              />
            </div>

            <div>
              <label
                htmlFor="dueDate"
                className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } mb-2`}
              >
                Due Date *
              </label>
              <div className="relative">
                <div
                  className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <Calendar className="w-5 h-5" />
                </div>
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  required
                  value={formData.dueDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`pl-10 w-full px-3 py-2 border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/dashboard/admin/payment-requests"
              className={`px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } rounded-lg transition-colors duration-200`}
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className={`flex items-center px-6 py-2 ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePaymentRequest;
